import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
} from 'recharts';
import { toast } from 'sonner';

const RANGES = [
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
  { key: 365, label: '365d' },
];

const usd = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);

/**
 * iter164 — MRR Trend Chart
 * Renders a rolling MRR line chart with 30d / 90d / 365d toggles.
 * Backfilled points are visually distinguished from live snapshots.
 */
export default function MrrTrendChart() {
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ series: [], current_mrr: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/admin/payments/mrr-trend?days=${days}`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) toast.error('Failed to load MRR trend');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  const stats = useMemo(() => {
    const s = data.series || [];
    if (s.length < 2) return { first: 0, last: 0, delta: 0, deltaPct: 0, backfillEnd: null };
    const first = s[0].mrr || 0;
    const last = s[s.length - 1].mrr || 0;
    const delta = last - first;
    const deltaPct = first > 0 ? (delta / first) * 100 : 0;
    // Find the last backfilled date so we can shade that band on the chart
    let backfillEnd = null;
    for (let i = s.length - 1; i >= 0; i--) {
      if (s[i].backfilled) {
        backfillEnd = s[i].date;
        break;
      }
    }
    return { first, last, delta, deltaPct, backfillEnd };
  }, [data]);

  const TrendIcon = stats.delta > 0 ? TrendingUp : stats.delta < 0 ? TrendingDown : Minus;
  const trendColor =
    stats.delta > 0 ? 'text-emerald-400' : stats.delta < 0 ? 'text-rose-400' : 'text-slate-300';

  return (
    <Card data-testid="mrr-trend-card">
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            MRR Trend
            <Badge variant="secondary" className="bg-blue-900/30 text-blue-200">
              live
            </Badge>
          </CardTitle>
          <CardDescription>
            Daily Monthly Recurring Revenue. Real tier prices, excludes admins &amp; demo user.
          </CardDescription>
        </div>
        <div className="flex items-center gap-1" data-testid="mrr-trend-range-toggle">
          {RANGES.map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant={days === r.key ? 'default' : 'outline'}
              onClick={() => setDays(r.key)}
              data-testid={`mrr-range-${r.key}`}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary strip */}
        <div className="flex items-center gap-6 mb-4">
          <div>
            <div className="text-xs text-slate-400">Current MRR</div>
            <div className="text-2xl font-bold text-red-400" data-testid="mrr-current">
              {usd(stats.last)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Start of window</div>
            <div className="text-lg font-semibold text-slate-100">{usd(stats.first)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Change</div>
            <div className={`text-lg font-semibold flex items-center gap-1 ${trendColor}`} data-testid="mrr-delta">
              <TrendIcon className="h-4 w-4" />
              {usd(Math.abs(stats.delta))}
              <span className="text-xs">({stats.deltaPct.toFixed(1)}%)</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center" data-testid="mrr-trend-loading">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (data.series?.length || 0) === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-slate-400" data-testid="mrr-trend-empty">
            No MRR data yet. Snapshots begin once at least one user is on a paid tier.
          </div>
        ) : (
          <div className="h-64" data-testid="mrr-trend-chart">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <LineChart data={data.series} margin={{ top: 5, right: 24, left: 0, bottom: 5 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) =>
                    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }
                  minTickGap={30}
                />
                <YAxis
                  stroke="#64748b"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                />
                {stats.backfillEnd && (
                  <ReferenceArea
                    x1={data.series[0].date}
                    x2={stats.backfillEnd}
                    strokeOpacity={0}
                    fill="#3b82f6"
                    fillOpacity={0.05}
                  />
                )}
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #1f2937', borderRadius: 8 }}
                  labelStyle={{ color: '#e2e8f0' }}
                  itemStyle={{ color: '#f87171' }}
                  formatter={(v, _n, item) => [
                    usd(v),
                    item?.payload?.backfilled ? 'MRR (approx.)' : 'MRR',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="mrr"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {stats.backfillEnd && (
          <p className="text-[11px] text-slate-500 mt-3" data-testid="mrr-backfill-note">
            Shaded region = backfilled estimate assuming current paying subscribers have been active
            since their signup date. Live daily snapshots begin{' '}
            {new Date(stats.backfillEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
